import React from 'react';

import { resolveConsent, setConsent, enableAnalytics, sweepClarityCookies, type ConsentState } from 'lib/basisConsent';

import { LEGAL_LINKS } from './basisLinks';

// [Basis chrome] El aviso de cookies del explorador es el de la landing.
//
// Port de frontend_landing/src/components/CookieConsent.astro: mismos dos
// niveles —aviso no bloqueante abajo, panel modal con trampa de foco—, mismos
// textos y mismos estilos. El CSS está en nextjs/basis-chrome.css, bajo
// `.bs-consent`, traducido de las utilidades de Tailwind igual que el resto del
// chrome, y con su variante oscura, que la landing no necesita.
//
// La decisión se comparte con la landing y el faucet a través de una cookie de
// dominio; la lógica entera vive en lib/basisConsent.ts. La analítica va
// encendida por defecto: el aviso informa y sigue estando el botón para
// apagarla, que es el único de los dos que hace algo.
//
// El enlace del pie (`data-consent-reopen`) reabre el panel. Lo busca este
// componente por atributo y no por prop porque el pie es otro árbol y no hay
// contexto entre los dos; es la misma delegación que hace la landing.

const TEXTOS = {
  title: 'Aviso de cookies',
  bodyLead: 'Usamos cookies para que todo funcione bien y para entender qué podemos mejorar. Al seguir navegando las aceptas. Puedes rechazarlas cuando quieras desde Preferencias, y ver el detalle en la ',
  bodyLink: 'Política de Cookies',
  bodyTail: '.',
  accept: 'Aceptar',
  preferences: 'Preferencias',
  dialog: {
    title: 'Preferencias de cookies',
    intro: 'Elige qué quieres permitir. Puedes cambiarlo cuando quieras desde el pie de página.',
    essentialTitle: 'Imprescindibles',
    essentialBadge: 'Siempre activas',
    essentialBody: 'Guardan esta misma elección para no volver a preguntarte. Es lo único que el sitio necesita almacenar por su cuenta.',
    analyticsTitle: 'Mejora de la experiencia',
    analyticsBody: 'Nos muestran qué partes funcionan bien y cuáles se atascan, para poder arreglarlas y que todo vaya más rápido. No se usan para publicidad.',
    policyLead: 'El detalle de cada cookie está en la ',
    policyLink: 'Política de Cookies',
    policyTail: '.',
    acceptAll: 'Aceptar todo',
    save: 'Guardar preferencias',
    close: 'Cerrar',
  },
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const BasisCookieConsent = () => {
  // Nada se pinta hasta que el efecto lee las cookies: en el servidor no hay
  // `document`, y un aviso renderizado en SSR parpadearía para quien ya decidió.
  const [ showBanner, setShowBanner ] = React.useState(false);
  const [ showDialog, setShowDialog ] = React.useState(false);
  const [ analytics, setAnalytics ] = React.useState(true);

  const decision = React.useRef<ConsentState>('unknown');
  const ask = React.useRef(false);
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const lastFocused = React.useRef<HTMLElement | null>(null);

  // -------------------------------------------------------------------------
  // Hueco al final de la página
  //
  // Exactamente lo que mide el aviso, medido y no estimado: su altura va de 79px
  // a 150px según el ancho. Sin esto la barra tapa el pie —y con él el enlace que
  // reabre estas mismas preferencias— y no hay scroll que lo libere, porque la
  // página ya está en su final.
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    const banner = bannerRef.current;
    if (!showBanner || !banner) {
      return;
    }
    // En linea y no en la hoja: el body lo estiliza Chakra desde Emotion, que
    // inyecta en runtime y ganaria a `body { padding-bottom }` de basis-chrome
    // por orden. Se guarda lo que hubiera para devolverlo al ocultar el aviso.
    // La misma altura va tambien a --consent-banner-space, que acota el menu
    // movil de la barra para que no quede debajo del aviso (como en la landing).
    const previous = document.body.style.paddingBottom;
    const sync = () => {
      const alto = `${ Math.ceil(banner.getBoundingClientRect().height) }px`;
      document.body.style.paddingBottom = alto;
      document.body.style.setProperty('--consent-banner-space', alto);
    };
    sync();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => {
        window.removeEventListener('resize', sync);
        document.body.style.paddingBottom = previous;
        document.body.style.removeProperty('--consent-banner-space');
      };
    }
    const observer = new ResizeObserver(sync);
    observer.observe(banner);
    return () => {
      observer.disconnect();
      document.body.style.paddingBottom = previous;
      document.body.style.removeProperty('--consent-banner-space');
    };
  }, [ showBanner ]);

  // -------------------------------------------------------------------------
  // Arranque
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    const resolved = resolveConsent();
    decision.current = resolved.state;
    ask.current = resolved.ask;

    // La analítica corre salvo que alguien la haya rechazado, aquí o en otro
    // subdominio.
    if (resolved.run) {
      enableAnalytics();
    } else {
      // Rechazada: no debe quedar ningún identificador de Clarity. Importa sobre
      // todo cuando el rechazo viene de otro subdominio, porque entonces la
      // etiqueta no llegó a cargarse en esta pestaña pero sus cookies siguen
      // aquí. El barrido va solo en esta rama: hacerlo también sin decisión
      // borraría el `_clck` que la etiqueta acaba de escribir.
      sweepClarityCookies();
    }

    // El aviso es independiente de lo anterior: se enseña siempre que no se haya
    // decidido nada en este sitio, con la analítica corriendo o no.
    setShowBanner(resolved.ask);
  }, []);

  const openDialog = React.useCallback(() => {
    lastFocused.current = document.activeElement as HTMLElement | null;
    // Sin decisión previa el interruptor arranca encendido; si ya se decidió,
    // refleja lo guardado. Un rechazo heredado de otro subdominio cuenta como
    // decidido: el panel enseña el estado real —apagado—, aunque el aviso se
    // vuelva a mostrar.
    setAnalytics(decision.current === 'unknown' ? true : decision.current === 'granted');
    setShowBanner(false);
    setShowDialog(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    setShowDialog(false);
    // Cerrar sin decidir no registra nada: el aviso vuelve a salir.
    setShowBanner(ask.current);
    lastFocused.current?.focus?.();
  }, []);

  const decide = React.useCallback((granted: boolean) => {
    decision.current = granted ? 'granted' : 'denied';
    ask.current = false;
    const mustReload = setConsent(decision.current);

    setShowBanner(false);
    setShowDialog(false);

    // Revocar con la etiqueta ya cargada: Clarity no se detiene cuando se le
    // niega el consentimiento, reinicia sin cookies y sigue enviando. Lo único
    // que la para de verdad es descartar el documento.
    if (!granted && mustReload) {
      window.location.reload();
    }
  }, []);

  // El enlace del pie vive en otro árbol; se engancha por delegación en
  // document, igual que en la landing. Aquí siempre hay algo que gobernar —el
  // identificador de Clarity es una constante, no una variable de entorno que
  // pueda llegar vacía—, así que no existe el caso de esconder el enlace.
  React.useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = (event.target as Element | null)?.closest?.('[data-consent-reopen]');
      if (target) {
        event.preventDefault();
        openDialog();
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [ openDialog ]);

  // Escape y trampa de foco, solo mientras el panel está abierto.
  React.useEffect(() => {
    if (!showDialog) {
      return;
    }
    const panel = panelRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
      .filter((el) => el.getClientRects().length > 0);

    focusables()[0]?.focus();

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const list = focusables();
      if (!list.length) {
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !panel?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel?.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeydown, true);
    return () => {
      document.removeEventListener('keydown', onKeydown, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [ showDialog, closeDialog ]);

  return (
    <div className="bs-chrome">
      { /* NIVEL 1 — Aviso. Barra inferior, no bloqueante. */ }
      { showBanner && (
        <div
          id="consent-banner"
          ref={ bannerRef }
          role="region"
          aria-label={ TEXTOS.title }
          aria-live="polite"
          className="bs-consent-banner"
        >
          <div className="bs-consent-banner__inner">
            <p className="bs-consent-banner__text">
              { TEXTOS.bodyLead }
              <a href={ LEGAL_LINKS.cookies }>{ TEXTOS.bodyLink }</a>{ TEXTOS.bodyTail }
            </p>
            <div className="bs-consent-banner__actions">
              <button
                type="button"
                className="bs-btn bs-btn--ghost bs-btn--small"
                data-consent-open
                onClick={ openDialog }
              >
                { TEXTOS.preferences }
              </button>
              <button
                type="button"
                className="bs-btn bs-btn--cyan bs-btn--small"
                data-consent-accept="banner"
                onClick={ () => decide(true) }
              >
                { TEXTOS.accept }
              </button>
            </div>
          </div>
        </div>
      ) }

      { /* NIVEL 2 — Panel de preferencias. Modal, con trampa de foco. */ }
      { showDialog && (
        <div id="consent-dialog" className="bs-consent-dialog">
          <div className="bs-consent-dialog__overlay" aria-hidden="true" data-consent-overlay onClick={ closeDialog }/>

          <div
            ref={ panelRef }
            id="consent-dialog-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-dialog-title"
            aria-describedby="consent-dialog-intro"
            className="bs-consent-dialog__panel"
          >
            <button
              type="button"
              className="bs-consent-dialog__close"
              data-consent-close
              aria-label={ TEXTOS.dialog.close }
              onClick={ closeDialog }
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>

            <h2 id="consent-dialog-title" className="bs-consent-dialog__title">{ TEXTOS.dialog.title }</h2>
            <p id="consent-dialog-intro" className="bs-consent-dialog__intro">{ TEXTOS.dialog.intro }</p>

            <div className="bs-consent-dialog__rows">
              { /* Imprescindibles: sin control, porque no hay nada que elegir. */ }
              <div className="bs-consent-row">
                <div className="bs-consent-row__body">
                  <p className="bs-consent-row__title">{ TEXTOS.dialog.essentialTitle }</p>
                  <p className="bs-consent-row__text">{ TEXTOS.dialog.essentialBody }</p>
                </div>
                <span className="bs-consent-row__badge">{ TEXTOS.dialog.essentialBadge }</span>
              </div>

              { /* Mejora de la experiencia: el único interruptor real. */ }
              <div className="bs-consent-row">
                <div className="bs-consent-row__body">
                  <p className="bs-consent-row__title">{ TEXTOS.dialog.analyticsTitle }</p>
                  <p className="bs-consent-row__text">{ TEXTOS.dialog.analyticsBody }</p>
                </div>
                <button
                  type="button"
                  id="consent-analytics-toggle"
                  role="switch"
                  aria-checked={ analytics }
                  aria-label={ TEXTOS.dialog.analyticsTitle }
                  className="bs-consent-switch"
                  onClick={ () => setAnalytics((on) => !on) }
                >
                  <span className="bs-consent-switch__knob"/>
                </button>
              </div>
            </div>

            <p className="bs-consent-dialog__policy">
              { TEXTOS.dialog.policyLead }
              <a href={ LEGAL_LINKS.cookies }>{ TEXTOS.dialog.policyLink }</a>{ TEXTOS.dialog.policyTail }
            </p>

            <div className="bs-consent-dialog__actions">
              <button
                type="button"
                className="bs-btn bs-btn--ghost bs-btn--small"
                data-consent-save
                onClick={ () => decide(analytics) }
              >
                { TEXTOS.dialog.save }
              </button>
              <button
                type="button"
                className="bs-btn bs-btn--cyan bs-btn--small"
                data-consent-accept="dialog"
                onClick={ () => decide(true) }
              >
                { TEXTOS.dialog.acceptAll }
              </button>
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};

export default React.memo(BasisCookieConsent);
